/**
 * Admin Sidebar Navigation
 * Desktop-first navigation sidebar for admin dashboard
 */

import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useAdminAuthStore } from '@/stores/adminAuthStore';
import { navigationItemsFor, superadminNavigationItems } from './navigationItems';

interface SidebarProps {
  compact?: boolean;
  onNavigate?: () => void;
}

export function Sidebar({ compact = false, onNavigate }: SidebarProps) {
  const location = useLocation();
  const admin = useAdminAuthStore((state) => state.admin);
  const isSuperAdmin = admin?.super_admin === true;

  return (
    <div className={cn('flex h-full flex-col bg-white border-r border-gray-200 transition-all duration-200', compact ? 'w-20' : 'w-64')}>
      {/* Logo */}
      <div className={cn('flex h-16 items-center border-b border-gray-200', compact ? 'justify-center px-2' : 'px-6')}>
        <h1 className={cn('text-[18px] font-normal tracking-[0.5px] text-[#1e1e1e] uppercase', compact && 'text-sm tracking-[1.5px]')}>
          {compact ? 'KB' : 'KOMBU'}
        </h1>
      </div>

      {/* Navigation */}
      <nav className={cn('flex-1 space-y-2 py-6 overflow-y-auto', compact ? 'px-2' : 'px-3')}>
        {navigationItemsFor(admin?.role).map((item) => {
          const isActive = location.pathname === item.href ||
            location.pathname.startsWith(item.href + '/');

          return (
            <Link
              key={item.name}
              to={item.href}
              onClick={onNavigate}
              className={cn(
                'flex min-h-11 items-center rounded-sm px-4 py-3 text-[14px] font-light transition-all',
                compact ? 'justify-center px-2' : 'gap-3',
                isActive
                  ? 'bg-[#1e1e1e] text-white'
                  : 'text-[#1e1e1e] hover:bg-[#eeeeee]'
              )}
              title={compact ? item.name : undefined}
            >
              <item.icon className="h-5 w-5" />
              {!compact && item.name}
            </Link>
          );
        })}

        {isSuperAdmin && (
          <>
            <div className={cn('pt-4 pb-2', compact ? 'px-2 text-center' : 'px-4')}>
              <span className={cn('text-[10px] uppercase tracking-wider text-gray-400', compact && 'text-[8px]')}>
                {compact ? 'SA' : 'Superadmin'}
              </span>
            </div>
            {superadminNavigationItems.map((item) => {
              const isActive = location.pathname === item.href ||
                location.pathname.startsWith(item.href + '/');

              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={onNavigate}
                  className={cn(
                    'flex min-h-11 items-center rounded-sm px-4 py-3 text-[14px] font-light transition-all',
                    compact ? 'justify-center px-2' : 'gap-3',
                    isActive
                      ? 'bg-[#1e1e1e] text-white'
                      : 'text-[#1e1e1e] hover:bg-[#eeeeee]'
                  )}
                  title={compact ? item.name : undefined}
                >
                  <item.icon className="h-5 w-5" />
                  {!compact && item.name}
                </Link>
              );
            })}
          </>
        )}
      </nav>

      {/* Footer */}
      <div className="border-t border-gray-200 p-4">
        <p className={cn('text-[12px] font-light text-[#9d9c9c] text-center', compact && 'text-[10px]')}>
          KOMBU v1.0
        </p>
      </div>
    </div>
  );
}
